/**
 * TechnologyCapabilityForm Component
 *
 * Reusable form for creating and editing technology capabilities
 */

import {
  EntityAutocompleteField,
  TextField,
  type CreateTechnologyCapabilityFormData,
} from '../forms';
import { createTechnologyCapabilitySchema } from '../forms/validation';
import { EntityForm } from '../shared';
import { TagPicker } from '../tags';
import { useAllTechnologyDomains } from '../../graphql/queries';
import '@tylertech/forge/card';
import '@tylertech/forge/icon';
import './TechnologyCapabilityForm.css';

export interface TechnologyCapabilityFormProps {
  /** Initial form values (for edit mode) */
  defaultValues?: Partial<CreateTechnologyCapabilityFormData>;
  /** Submit handler */
  onSubmit: (data: CreateTechnologyCapabilityFormData) => void | Promise<void>;
  /** Cancel handler */
  onCancel: () => void;
  /** Is form submitting */
  isSubmitting?: boolean;
  /** Form mode */
  mode: 'create' | 'edit';
}

const CardElement = 'forge-card' as React.ElementType;

/**
 * TechnologyCapability create/edit form with validation
 *
 * @example
 * ```tsx
 * <TechnologyCapabilityForm
 *   mode="create"
 *   onSubmit={handleCreate}
 *   onCancel={() => navigate('/technology-capabilities')}
 * />
 * ```
 */
export function TechnologyCapabilityForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
}: TechnologyCapabilityFormProps): JSX.Element {
  // Full catalog (all pages, not one capped server page) so the domain
  // picker can search/select any existing Technology Domain (issue #764).
  const { data: domainsData } = useAllTechnologyDomains();
  const domainOptions = (domainsData?.technologyDomains?.edges ?? []).map((edge) => ({
    value: edge.node.entityId,
    label: edge.node.name,
  }));

  return (
    <EntityForm<CreateTechnologyCapabilityFormData>
      schema={createTechnologyCapabilitySchema}
      baseValues={{
        tags: [],
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Create Technology Capability"
      formClassName="technology-capability-form"
    >
      {({ register, formState: { errors }, values, setValue }) => {
        const tags = values.tags || [];

        return (
          <>
            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Basic Information</h2>

                <TextField
                  label="Name"
                  {...register('name')}
                  value={values.name || ''}
                  error={errors.name}
                  required
                  placeholder="e.g., Identity & Access Management"
                  helperText="A descriptive name for the technology capability"
                />

                <TextField
                  label="Description"
                  {...register('description')}
                  value={values.description || ''}
                  error={errors.description}
                  placeholder="Brief description of the technology capability"
                  helperText="Optional description (max 2000 characters)"
                />

                <EntityAutocompleteField
                  name="domainId"
                  label="Domain"
                  options={domainOptions}
                  value={values.domainId || ''}
                  onChange={(domainId) => setValue('domainId', domainId, { shouldValidate: true })}
                  error={errors.domainId}
                  placeholder="Search technology domains…"
                  helperText="Optional parent technology domain"
                  emptyMessage="No technology domains match your search"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Additional Information</h2>

                <TagPicker
                  id="technology-capability-tag-picker"
                  label="Tags"
                  selectedTagIds={tags}
                  onChange={(newIds) => setValue('tags', newIds)}
                />

                <TextField
                  label="Notes"
                  {...register('notes')}
                  value={values.notes || ''}
                  error={errors.notes}
                  placeholder="Additional notes about this technology capability"
                  helperText="Optional notes (max 5000 characters)"
                />
              </div>
            </CardElement>
          </>
        );
      }}
    </EntityForm>
  );
}
